<template>
    <v-card ref="cardBody" class="full-card" elevation="3" style="overflow: auto">
        <v-btn-toggle v-model="currentView" mandatory density="compact" class="position-absolute view-toggle-group"
                      style="right: 0;z-index: 1001;">
            <v-btn value="map" size="x-small" icon>
                <v-icon>mdi-map</v-icon>
            </v-btn>
            <v-btn value="table" size="x-small" icon>
                <v-icon>mdi-table</v-icon>
            </v-btn>
        </v-btn-toggle>

        <div v-if="currentView === 'map'" class="map-controls-btn-group">
            <v-btn @click="fitAllDevices" size="x-small" icon color="white" elevation="2" class="mb-1"
                   title="Fit all devices">
                <v-icon>mdi-fit-to-screen</v-icon>
            </v-btn>
            <v-btn @click="toggleFullscreen" size="x-small" icon color="white" elevation="2" title="Toggle fullscreen">
                <v-icon>{{ isFullscreen ? 'mdi-fullscreen-exit' : 'mdi-fullscreen' }}</v-icon>
            </v-btn>
        </div>

        <v-card-text class="pa-0">
            <div v-if="currentView === 'table'" style="width:100%;">
                <devices-data-table :devices="filteredDevices"
                                    :headers="headers"
                                    :selected-device="selectedDevice"
                />
            </div>
            <div v-else style="position: relative;" @click="onMapClick">
                <div class="fleet-filter-overlay" @click.stop>
                    <v-btn v-if="!showFilters"
                           @click="openFilters"
                           size="x-small"
                           icon
                           color="white"
                           elevation="2">
                        <v-badge v-if="activeFilter" :content="activeFilterCount" color="primary">
                            <v-icon>mdi-filter-variant</v-icon>
                        </v-badge>
                        <v-icon v-else>mdi-filter-variant</v-icon>
                    </v-btn>
                    <div v-else class="filter-chips-container">
                        <v-btn @click="closeFilters" size="x-small" icon variant="text" class="me-1">
                            <v-icon>mdi-close</v-icon>
                        </v-btn>
                        <v-chip v-for="chip in fleetChips" :key="chip.key"
                                :color="chip.count > 0 ? chip.color : undefined"
                                :variant="activeFilter === chip.key ? 'flat' : (chip.count > 0 ? 'tonal' : 'outlined')"
                                :prepend-icon="chip.icon"
                                density="comfortable"
                                size="small"
                                class="me-1 cursor-pointer"
                                @click="toggleFilter(chip.key)">
                            {{ chip.label }}: {{ chip.count }}
                        </v-chip>
                    </div>
                </div>
                <devices-map ref="devicesMapRef"
                             :devices="filteredDevices"
                             :all-devices="props.devices"
                             :height="mapHeight"
                             :zoomToDevice="zoomToDevice"
                             :nearestToDevice="nearestToDevice"
                             :selected-device="selectedDevice"
                             @selected-marker="(device)=>$emit('selectDevice',device)"
                             @map-clicked="onMapClick"
                />
            </div>
        </v-card-text>
    </v-card>
</template>

<script setup>
import {computed, onBeforeUnmount, onMounted, ref} from "vue";
import DevicesMap from "@/components/cards/devices/DevicesMap.vue";
import DevicesDataTable from "@/components/cards/devices/DevicesDataTable.vue";
import {isMoving, isIdle, isStationary, isNonReporting, isSharingOff, isLowBattery, filterDevicesByFleetStatus} from "@/composables/fleetStatus";

const cardBody = ref();
const devicesMapRef = ref();
const mapHeight = ref(null);
const currentView = ref('map');
const showFilters = ref(false);
const isFullscreen = ref(false);
const activeFilter = ref(null);
let autoCloseTimer = null;
const emits = defineEmits(['selectDevice']);
const props = defineProps(['selectedDevice', 'devices', 'headers', 'zoomToDevice', 'nearestToDevice']);

const toggleFullscreen = () => {
    const el = cardBody.value?.$el;
    if (!el) return;
    if (!document.fullscreenElement) {
        el.requestFullscreen().catch(err => console.error('Fullscreen request failed:', err));
    } else {
        document.exitFullscreen();
    }
};

const onFullscreenChange = () => {
    isFullscreen.value = !!document.fullscreenElement;
    updateMapHeight();
};

const fitAllDevices = () => {
    devicesMapRef.value?.fitAllDevices();
};

const fleetChips = computed(() => {
    const devices = props.devices || [];
    return [
        {key: 'total', label: 'Total', color: 'primary', icon: 'mdi-car-multiple', count: devices.length},
        {key: 'moving', label: 'Moving', color: 'green', icon: 'mdi-car-connected', count: devices.filter(d => isMoving(d) || isIdle(d)).length},
        {key: 'idle', label: 'Idling', color: 'amber', icon: 'mdi-car-clock', count: devices.filter(isIdle).length},
        {key: 'stationary', label: 'Stationary', color: 'grey', icon: 'mdi-car-brake-parking', count: devices.filter(isStationary).length},
        {key: 'non_reporting', label: 'Offline', color: 'red', icon: 'mdi-signal-off', count: devices.filter(isNonReporting).length},
        // Separate chip, not folded into Offline. A paused member chose this and
        // nothing is wrong; Offline says we cannot reach them and something may
        // be. Counting them together told a family their phone had gone dark
        // when it had checked in minutes earlier.
        {key: 'sharing_off', label: 'Sharing paused', color: 'blue-grey', icon: 'mdi-pause-circle-outline', count: devices.filter(isSharingOff).length},
        {key: 'low_battery', label: 'Low Battery', color: 'orange', icon: 'mdi-battery-low', count: devices.filter(isLowBattery).length},
    ];
});

const activeFilterCount = computed(() => {
    if (!activeFilter.value) return 0;
    const chip = fleetChips.value.find(c => c.key === activeFilter.value);
    return chip ? chip.count : 0;
});

const filteredDevices = computed(() => filterDevicesByFleetStatus(props.devices, activeFilter.value));

const startAutoCloseTimer = () => {
    clearAutoCloseTimer();
    autoCloseTimer = setTimeout(() => {
        showFilters.value = false;
    }, 10000);
};

const clearAutoCloseTimer = () => {
    if (autoCloseTimer) {
        clearTimeout(autoCloseTimer);
        autoCloseTimer = null;
    }
};

const openFilters = () => {
    showFilters.value = true;
    startAutoCloseTimer();
};

const closeFilters = () => {
    showFilters.value = false;
    clearAutoCloseTimer();
};

const onMapClick = () => {
    if (showFilters.value) {
        closeFilters();
    }
};

const toggleFilter = (key) => {
    activeFilter.value = activeFilter.value === key ? null : key;
    closeFilters();
};

const updateMapHeight = () => {
    if (isFullscreen.value) {
        mapHeight.value = '100vh';
    } else {
        // Below 960px neither .full-card nor .max-height applies (both live in
        // that media query), so no ancestor has a resolved height and the card
        // measures as content - which is itself the collapsed map. That
        // circularity used to land on a hardcoded 260px. Derive a viewport
        // height instead, so removing the alerts panel actually buys map room
        // on a phone rather than just removing a panel.
        const measured = cardBody.value?.$el?.offsetHeight ?? 0;
        mapHeight.value = (measured > 320
            ? measured
            : Math.max(360, Math.round(window.innerHeight * 0.6))) + 'px';
    }
};

onMounted(() => {
    updateMapHeight()
    window.addEventListener('resize', updateMapHeight)
    document.addEventListener('fullscreenchange', onFullscreenChange)
})

onBeforeUnmount(() => {
    window.removeEventListener('resize', updateMapHeight)
    document.removeEventListener('fullscreenchange', onFullscreenChange)
    clearAutoCloseTimer();
})
</script>

<style scoped>
.fleet-filter-overlay {
    position: absolute;
    top: 8px;
    left: 8px;
    z-index: 1001;
    display: flex;
    align-items: center;
}

.filter-chips-container {
    display: flex;
    align-items: center;
    background: rgba(255, 255, 255, 0.95);
    border-radius: 8px;
    padding: 4px 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
    flex-wrap: wrap;
    max-width: calc(100% - 16px);
    gap: 2px;
}

.map-controls-btn-group {
    position: absolute;
    bottom: 8px;
    right: 8px;
    z-index: 1001;
    display: flex;
    flex-direction: column;
    align-items: center;
}

.view-toggle-group {
    border-radius: 4px;
    overflow: hidden;
}
</style>
